// models.ts

import type { GeocodingFeature } from "./mapboxModels"

// Onboarding properties interface
export interface UserOnboarding {
    profileCompletionPromptDismissed?: boolean
    clubSharePromptDismissed?: boolean
    // Add more onboarding properties here as needed in the future
    // e.g., welcomeTourCompleted?: boolean
    // e.g., firstEventCreated?: boolean
}

export interface UserProfile {
    id: string
    first_name?: string
    last_name?: string
    pronouns?: string
    username?: string
    display_name?: string
    display_name_updated_at?: string
    bio?: string
    avatar_small?: string
    avatar_medium?: string
    avatar_full?: string
    contact_info?: string
    location_name?: string | null
    location_object?: GeocodingFeature | null // allow null
    latitude?: number
    longitude?: number
    membership_type?: string
    email?: string
    phone_number?: string
    web_personal?: string
    web_business?: string
    instagram?: string
    tiktok?: string
    strava?: string
    linkedin?: string
    job_title?: string
    birthdate?: string // ISO date string
    education_name?: string
    available_irl?: boolean
    show_year_of_birth?: boolean
    show_zodiac?: boolean
    created_at: string
    updated_at: string
    outgoing_requests?: string[] // Array of club IDs that the user has requested to join
    hidden_pending_requests?: string[] // Array of club IDs whose pending request banners have been hidden
    dismissed_club_recommendations?: string[] // Array of club IDs that the user has dismissed from recommendations
    user_topics?: UserTopic[] // Optional field populated when explicitly requested
    onboarding?: UserOnboarding // Onboarding-related flags and properties
    onboarding_complete?: boolean // Indicates whether user has completed the full onboarding flow
    notification_preferences?: {
        clubs?: {
            [clubId: string]: {
                posts?: "everything" | "comments_likes_only" | "nothing"
                events?: "everything" | "reminders_only" | "nothing"
            }
        }
    }
    clubs_count?: number // Number of clubs the user is a member of
    events_attended_count?: number // Number of events the user has RSVP'd "yes" to
    is_active?: boolean // Whether the account is active (false = deactivated)
    is_banned?: boolean // Whether the user has been banned
}

// Topic model
export interface Topic {
    id: string
    name: string
    emoji: string | null
    category_id: string | null
    topic_categories?: {
        id: string
        name: string
    }
}

// Subtopic model
export interface Subtopic {
    id: string
    name: string
    parent_topic_id: string
    parent_topic?: Topic
}

// User topic association
export interface UserTopic {
    id: string
    user_id: string
    topic_id: string | null
    subtopic_id: string | null
    created_at: string
    topics?: Topic // Optional expanded topic data
    subtopics?: Subtopic // Optional expanded subtopic data
}

// Club topic association
export interface ClubTopic {
    id: string
    club_id: string
    topic_id: string | null
    subtopic_id: string | null
    created_at: string
    topics?: Topic // Optional expanded topic data
    subtopics?: Subtopic // Optional expanded subtopic data
}

export interface Club {
    id?: string // This is optional because it's generated by Supabase, so will be undefined when creating a new club
    name: string
    slug?: string
    short_url?: string
    description?: string
    note?: string
    note_updated_by?: string // Reference to the user who last updated the note
    location_name?: string | null
    location_object?: GeocodingFeature | null // allow null
    is_provisioned_by_superclub?: boolean
    superclub_id?: string | null
    theme?: string
    private?: boolean
    enable_link?: boolean
    post_permission?: "everyone" | "managers" // Control who can create posts
    hang_permission?: "everyone" | "managers" // Control who can create hangs
    primary_image_small?: string
    primary_image_medium?: string
    primary_image_full?: string
    link_preview_image?: string // Static image for link previews (extracted first frame for GIFs)
    created_at: string
    updated_at: string
    invite_code?: string
    incoming_requests?: string[]
    guidelines?: string // Club-specific guidelines
    directory_enabled?: boolean // Whether club can be discovered via interests
    show_member_count?: boolean // Whether member count is visible publicly (defaults to true)
    club_topics?: ClubTopic[] // Optional field populated when explicitly requested
    member_count?: number
    creator_id?: string // Reference to the user who created the club (the owner)
}

// Define types for Club Kit items
export interface ClubLink {
    id: string
    club_id: string
    name: string | null
    url: string
    creator_id: string | null
    created_at: string
}

export interface ClubFile {
    id: string
    club_id: string
    name: string
    file_url: string
    filename: string
    mime_type: string
    upload_date: string
    creator_id: string | null
}

export interface ClubMedia {
    id: string
    club_id: string
    caption: string | null
    mime_type: string | null
    media_type: "image" | "video"
    media_url: string
    filename: string
    thumbnail_url: string | null
    thumbnail_filename: string | null
    upload_date: string
    creator_id: string | null
}

export interface ClubInfoData {
    links: ClubLink[]
    files: ClubFile[]
    media: ClubMedia[]
}

// Public data interface exposing only limited columns.
// Note: This table intentionally does NOT include location_object or coordinates for privacy.
export interface ClubPublicData {
    id: string
    name: string
    description?: string
    location_name?: string
    public_location_display?: string // Privacy-friendly town-level location (e.g., "Shelburne, VT")
    theme?: string
    private?: boolean
    primary_image_small?: string
    primary_image_medium?: string
    primary_image_full?: string
    invite_code?: string
    member_count?: number
    show_member_count?: boolean // Whether member count is visible publicly (defaults to true)
    is_accepting_members?: boolean // Computed field - false if club is over member or cohost limits
}

// Define the club role type
export type ClubRole = "owner" | "cohost" | "member"

// Type-safe definition for club roles
export interface ClubRoleDefinition {
    key: ClubRole
    label: string
    description: string
}

export const CLUB_ROLES: ClubRoleDefinition[] = [
    {
        key: "owner",
        label: "Owner",
        description: "Manages the club — including deleting it or transferring ownership. Your TinyClub plan sets limits for co-hosts, members, and fees.",
    },
    {
        key: "cohost",
        label: "Co-host",
        description: "Helps run the club — creating events, managing hangs, and connecting with members. The number of co-hosts depends on the owner's plan.",
    },
    {
        key: "member",
        label: "Member",
        description: "Participates in club activities and events as part of the community.",
    },
]

// Utility functions for role checking
// Accept either a role string or an object with a role property (like ClubMember)
type RoleInput = ClubRole | string | { role?: ClubRole | string } | null | undefined

export const isOwner = (input: RoleInput): boolean => {
    if (!input) return false
    const role = typeof input === "object" ? input.role : input
    return role === "owner"
}

export const isCohost = (input: RoleInput): boolean => {
    if (!input) return false
    const role = typeof input === "object" ? input.role : input
    return role === "cohost"
}

export const isOwnerOrCohost = (input: RoleInput): boolean => {
    if (!input) return false
    const role = typeof input === "object" ? input.role : input
    return role === "owner" || role === "cohost"
}

// Membership tier limits interface (from database)
export interface MembershipTierLimits {
    tier_name: string
    max_members: number
    max_cohosts: number
    display_name: string
    description?: string
    created_at: string
    updated_at: string
}

export type MembershipTier = "free" | "plus" | "pro"

export interface ClubMember {
    id: string
    club_id: string
    user_id: string
    role?: ClubRole // Server accepts any string, but client restricts to ClubRole
    display_name_override?: string
    joined_at: string
    updated_at: string
    /** Timestamp when user pinned this club. NULL = not pinned. */
    pinned_at?: string | null
    /** How the member was added to the club */
    source?: "direct" | "invite_link" | "import" | "auto_match"
    /** Joined user profile data for mention mapping */
    user_profiles?: {
        username?: string | null
        first_name?: string | null
        last_name?: string | null
        display_name?: string | null
    } | null
}

export type InvitationStatus = "pending" | "sent" | "accepted" | "expired" | "revoked"
export type InvitationSource = "meetup_import" | "screenshot_import" | "manual"

export interface ClubInvitation {
    id: string
    club_id: string
    invited_by: string
    email?: string | null
    name: string
    first_name?: string | null
    last_name?: string | null
    intended_role: "member" | "cohost"
    invite_token: string
    status: InvitationStatus
    source: InvitationSource
    source_metadata?: Record<string, unknown> | null
    accepted_by?: string | null
    accepted_at?: string | null
    expires_at?: string | null
    created_at: string
    updated_at: string
}

/** What a club data export covers: the whole club, or just the requester's own data */
export type ClubExportScope = "club" | "self"

/** Lifecycle of a club data export job */
export type ClubExportStatus = "pending" | "processing" | "ready" | "failed"

/** A single club data export request and its result (public.club_export_jobs) */
export interface ClubExportJob {
    id: string
    club_id: string
    requested_by: string
    scope: ClubExportScope
    include_media: boolean
    status: ClubExportStatus
    progress?: Record<string, unknown> | null
    error_message?: string | null
    storage_key?: string | null
    download_url?: string | null
    file_size_bytes?: number | null
    expires_at?: string | null
    created_at: string
    updated_at: string
    completed_at?: string | null
}

export enum EventType {
    event = "event",
    hang = "hang",
}

export interface Event {
    id: string
    club_id: string | null
    creator_id?: string
    title?: string
    event_type: EventType
    description?: string
    location_name?: string | null
    location_object?: GeocodingFeature | null // allow null
    is_virtual?: boolean // If true, event uses a link instead of physical location
    virtual_link_url?: string | null // URL for virtual events
    is_all_day?: boolean
    start_time?: string | null
    end_time?: string | null
    repeat_rule?: EventRepeatRule | null
    primary_image_small?: string
    primary_image_medium?: string
    primary_image_full?: string
    link_preview_image?: string // Static image for link previews (extracted first frame for GIFs)
    created_at: string
    updated_at: string
    timezone?: string
    allows_public_link?: boolean // If true, the event can be accessed by anyone with the link
    allows_quick_replies?: boolean // If true, allows quick replies for hangs
    directory_enabled?: boolean // If true, the event can be discovered via recommendations/contact notifications
    hide_attendees?: boolean // If true, only the creator can see the attendee list
}

export interface EventOccurrence {
    id: string
    event_id: string
    occurrence_start: string // ISO date string
    occurrence_end: string // ISO date string
    canceled: boolean
    created_at: string
    updated_at: string
    invite_code?: string
    event_attendees?: EventAttendee[]
    event_reactions?: EventReaction[]
    reaction_counts?: EventReactionCount[]
    // Embedded event details aliased as "rootEvent"
    rootEvent: Event
}

export interface EventAttendee {
    id: string
    occurrence_id: string | null
    user_id: string | null
    rsvp_status?: RsvpState
    created_at: string
}

export interface EventReaction {
    id: string
    occurrence_id: string
    user_id: string
    reaction_type: string
    created_at: string
}

export interface EventReactionCount {
    reaction_type: string
    count: number
    user_reacted: boolean
}

export enum EventRepeatRule {
    daily = "daily",
    weekly = "weekly",
    biweekly = "biweekly",
    monthly = "monthly",
    yearly = "yearly",
}

export enum RsvpState {
    yes = "yes",
    maybe = "maybe",
    no = "no",
    none = "none",
}

export type PublicKeys = {
    mapbox_public_key: string
    turnstile_site_key: string
}

export type NotificationType =
    | "new_member"
    | "member_left"
    | "new_event"
    | "event_update"
    | "event_canceled"
    | "new_hang"
    | "hang_update"
    | "hang_canceled"
    | "hang_reaction"
    | "hang_response"
    | "event_reminder"
    | "membership_request"
    | "membership_approved"
    | "club_update"
    | "new_post"
    | "post_comment"
    | "post_like"
    | "comment_like"
    | "system"
    | "contact_joined"
    | "contact_joined_club"
    | "contact_started_club"
    | "contact_rsvp_event"
    | "contact_created_event"
    | "mention"

export interface Notification {
    id: string
    user_id: string
    creator_id: string
    title: string
    body: string
    created_at: string
    is_read: boolean
    target_url?: string | null
    image_url?: string | null
    notification_type: NotificationType
    related_entity_id?: string | null
    related_entity_type?: string | null
}

// Poll option for a poll
export interface PollOption {
    id: string
    poll_id: string
    option_text: string
    vote_count: number
    sort_order: number
}

// Poll attached to a post
export interface Poll {
    id: string
    post_id: string
    duration_hours: number
    closes_at: string
    is_closed: boolean
    total_votes: number
    created_at: string
    options: PollOption[]
    user_vote_option_id?: string | null // Current user's vote (populated via query)
}

export interface PostMedia {
    id: string
    post_id: string
    kind: "image" | "video"
    image_small?: string | null
    image_medium?: string | null
    image_full?: string | null
    video_url?: string | null
    width: number | null
    height: number | null
    aspect_ratio: number | null
    duration: number | null
    sort_order: number
    created_at: string
}

export interface Post {
    id: string
    author_id: string | null // Nullable when author is deleted
    club_id: string | null
    body_text: string
    allow_comments: boolean
    pinned: boolean
    pinned_date: string | null
    deleted_by_user: boolean
    moderated_deleted: boolean
    like_count: number
    comment_count?: number // Computed from comments query, not stored in DB
    created_at: string
    updated_at: string
    liked?: boolean
    post_media: PostMedia[]
    location_name?: string | null
    location_object?: GeocodingFeature | null
    poll?: Poll | null // Optional poll attachment
}

export interface Comment {
    id: string
    post_id: string
    user_id: string | null // Nullable when commenter is deleted
    body_text: string
    deleted_by_user: boolean
    moderated_deleted: boolean
    created_at: string
    like_count: number
    liked?: boolean
    author_liked?: boolean
}

export interface ReservedUsername {
    id: string
    username: string
    reason?: string | null
    created_at: string
    created_by?: string | null
}

// Membership tier definition
export interface ClubMembershipTier {
    id: string
    name: string // e.g., "standard", "gold", "platinum"
    display_name: string // e.g., "Standard Member", "Gold Member"
    description?: string
    price_once?: number
    price_monthly?: number
    price_yearly?: number
    trial_days?: number
    features?: string[] // List of features/benefits for this tier
    sort_order: number // For displaying tiers in order
}

// Payment Settings for clubs
export interface ClubPaymentSettings {
    id?: string
    club_id: string
    membership_enabled: boolean
    membership_price_once?: number // Default/basic tier price (backwards compatible)
    membership_price_monthly?: number
    membership_price_yearly?: number
    membership_description?: string
    membership_trial_days?: number
    membership_tiers?: ClubMembershipTier[] // Optional: advanced tier system
    dues_enabled: boolean
    dues_price_once?: number
    dues_price_monthly?: number
    dues_price_yearly?: number
    dues_description?: string
    created_at?: string
    updated_at?: string
}

// User subscription to a club
export interface UserClubSubscription {
    id: string
    user_id: string
    club_id: string
    type: "membership"
    tier_id?: string // Optional: specific tier if using tier system
    tier_name?: string // Cached tier name for quick access
    status: "active" | "trialing" | "past_due" | "canceled" | "incomplete" | "incomplete_expired" | "unpaid"
    interval?: "monthly" | "yearly"
    stripe_subscription_id?: string
    stripe_customer_id?: string
    stripe_product_id?: string // Track which Stripe product/tier
    current_period_start?: string
    current_period_end?: string
    trial_end?: string
    canceled_at?: string
    created_at: string
    updated_at: string
}

export interface UserPayoutAccount {
    id: string
    user_id: string
    account_type: "personal" | "home" | "business"
    nickname?: string | null
    stripe_account_id?: string | null
    stripe_account_status?: "pending" | "incomplete" | "active" | "restricted" | "disabled" | null
    stripe_account_charges_enabled?: boolean
    stripe_account_payouts_enabled?: boolean
    stripe_account_details_submitted?: boolean
    stripe_account_country?: string | null
    stripe_account_last4?: string | null
    stripe_account_created_at?: string | null
    created_at: string
    updated_at: string
}

export interface UserBlock {
    id: string
    blocker_id: string
    blocked_id: string
    created_at: string
}

// Email quota tracking for club email sends
export interface ClubEmailQuota {
    used: number
    limit: number
    remaining: number
    ownerTier: MembershipTier
}
